/* eslint-disable no-param-reassign */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  GridCellParams,
  GridColParams,
  DataGrid,
  GridRowId,
  GridRowModel,
  GridSelectionModelChangeParams,
} from '@material-ui/data-grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { blue, blueGrey, green, grey } from '@material-ui/core/colors';
import { Theme } from '@material-ui/core/styles';
import CheckBox from '../Checkbox';
import { IColDef, ITable, ITabledata } from './type';

const isUndefined = (value: unknown) => value === undefined;

/**
 * 1. data 自動插入項次 itemNo, 可直接於 column 使用此欄位
 * 2. checkboxSelection 開啟才有選取 row 的 style
 * 3. checkboxSelection 不能條件顯示 checkBox 故將其 display: none，並重新撰寫，
 *    若需使用於 column 增加同 selectedField 欄位即會產生
 * 4. custom control，若有排版問題，要設定 inputProps={{ style: { fontSize: '1em' } }}
 * 5. custom input 請使用 defaultValue, 只 rerender 自身 component,
 *    onChange 時更新 row 裡的資料但不觸發 rerender，避免效能問題
 * 6. custom datePicker, checkBox 使用 value,
 *    並於 onChange 時執行 syncData() 以觸發 rerender
 */
const Table = (props: ITable): JSX.Element => {
  const {
    rootProps = {},
    idField = 'id',
    data,
    page: pageTemp = 0,
    pageSize: pageSizeTemp = 10,
    disableColumnMenu = true,
    selectedField = 'selected',
    columns: columnsTemp,
    rowsPerPageOptions = [10, 20, 50],
    density = 'compact',
    headerAlign = 'center',
    align = 'center',
    onPageChange,
    onPageSizeChange,
    onSelectionModelChange,
    renderCell,
    ...other
  } = props;

  const [rows, setRows] = useState<GridRowModel[]>([]);
  const [columns, setColumns] = useState<IColDef[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const [page, setPage] = useState(pageTemp);
  const [pageSize, setPageSize] = useState(pageSizeTemp);
  const rowStart = page * pageSize;
  const rowEnd = (page + 1) * pageSize - 1;
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // id 是必填
    // 插入項次(itemNo)
    (data as ITabledata[]).forEach((obj, idx) => {
      obj.id = String(obj[idField] || idx);
      obj.itemNo = idx + 1;
      obj[selectedField] = false;
    });
    setRows(
      data as ITabledata[]
      // (data as ITabledata[]).map(
      //   (obj, i) =>
      //     ({
      //       id: obj[idField] || i,
      //       itemNo: i + 1,
      //       [selectedField]: i % 2 === 0 ? false : undefined,
      //       ...obj,
      //     } as RowModel)
      // )
    );
  }, [data]);

  // 重製 selectionCheckbox (header)
  const selectedRenderHeader = (params: GridColParams) => {
    const { colDef } = params;
    const currentRows = rows.filter(
      (v, idx) => idx >= rowStart && idx <= rowEnd
    );
    const someTrue = currentRows.some((obj) => obj[selectedField] === true);
    const somefalse = currentRows.some((obj) => obj[selectedField] === false);
    return (
      <CheckBox
        color="primary"
        label={colDef.headerName}
        checked={someTrue}
        indeterminate={someTrue && somefalse}
        onChange={(e) => {
          const { checked } = e.target;

          const selectedValue = (obj: GridRowModel) =>
            isUndefined(obj[selectedField]) ? undefined : checked;

          setRows((prev) =>
            prev.map((obj, idx) => ({
              ...obj,
              [selectedField]:
                idx >= rowStart && idx <= rowEnd
                  ? selectedValue(obj)
                  : obj[selectedField],
            }))
          );
        }}
      />
    );
  };

  // 重製 selectionCheckbox (cell))
  const selectedRenderCell = (params: GridCellParams) => {
    const { row } = params;
    return (
      <>
        {/** 該欄位 undefined 時，不會產生 checkbox */}
        {!isUndefined(row[selectedField]) && (
          <CheckBox
            color="primary"
            checked={row[selectedField]}
            onChange={(e) => {
              const { checked } = e.target;
              row[selectedField] = checked;
              setRows((prev) => [...prev]);
            }}
          />
        )}
      </>
    );
  };

  const handleSelectionModelChange = (
    param: GridSelectionModelChangeParams
  ) => {
    const rowsTemp = rows.map((obj) => ({
      ...obj,
      [selectedField]: isUndefined(obj[selectedField])
        ? undefined
        : param.selectionModel.some((rowId) => rowId === String(obj.id)),
    }));

    const selected = rowsTemp
      .filter((obj) => obj[selectedField])
      .map((obj) => String(obj.id));

    if (
      // 有不同時才 setSelectionModel, 避免 loop
      JSON.stringify(selected) !== JSON.stringify(selectionModel) ||
      // param 可能會選到 無checkbox 的 row, 需觸發 setSelectionModel
      JSON.stringify(selected) !== JSON.stringify(param.selectionModel)
    ) {
      setRows(rowsTemp);
      // setSelectionModel(selected); // useEffect 有寫更新 setSelectionModel
    }

    if (onSelectionModelChange) {
      onSelectionModelChange(param);
    }
  };

  /** 外部 page 改變時，同步更新 page */
  useEffect(() => {
    setPage(pageTemp);
  }, [pageTemp]);

  /** 外部 pageSize 改變時，同步更新 pageSize */
  useEffect(() => {
    setPageSize(pageSizeTemp);
  }, [pageSizeTemp]);

  /** page 或 pageSize 時，要更新 selectedRenderHeader，checkBox 狀態才會更新 */
  useEffect(() => {
    setColumns((prev) =>
      prev.map((obj) =>
        obj.field === selectedField
          ? {
              ...obj,
              renderHeader: selectedRenderHeader,
              renderCell: selectedRenderCell,
              sortable: false,
            }
          : obj
      )
    );
  }, [page, pageSize]);

  const syncData = () => {
    setRows((prev) => [...prev]);
    // 已有 useEffect 監聽 rows, 並更新 data
  };

  useEffect(() => {
    // 有 selectedField 時, 加入 checkbox (selectedRenderHeader, selectedRenderCell)
    setColumns(
      columnsTemp.map((obj) => {
        // 重製 checkboxSelection
        if (obj.field === selectedField) {
          return {
            ...obj,
            renderHeader: selectedRenderHeader,
            renderCell: selectedRenderCell,
            sortable: false,
          };
        }

        // 使用者定義 renderHeader, renderCell
        if (obj.customCell) {
          return {
            ...obj,
            // renderHeader: renderHeader,
            renderCell: !renderCell
              ? undefined
              : (params) => renderCell(params, syncData),
          };
        }

        return obj;
      })
    );

    // 更新至 data
    rows.forEach((obj) => {
      const idx = (data as ITabledata[]).findIndex((d) => d.id === obj.id);
      if (idx > -1) {
        Object.keys(obj).forEach((key) => {
          (data as ITabledata[])[idx][key] = obj[key];
        });
      }
    });

    // setSelectionModel
    const selected = rows
      .filter((obj) => obj[selectedField])
      .map((obj) => String(obj.id));
    if (
      // 有不同時才 setSelectionModel, 避免 loop
      JSON.stringify(selected) !== JSON.stringify(selectionModel)
    ) {
      setSelectionModel(selected);
    }
  }, [rows]);

  // height: 0, width: 0 bug
  // useLayoutEffect = componentWillMount, 會影響效能，少用
  useLayoutEffect(() => {
    const gridDiv = divRef.current;
    if (gridDiv) {
      const gridEl = gridDiv.querySelector('div');
      if (gridEl) {
        (gridEl as HTMLDivElement).style.height = '';
        (gridEl as HTMLDivElement).style.width = '';
      }
    }
  });

  const classes = makeStyles((theme: Theme) => ({
    root: {
      '& .MuiDataGrid-colCellCheckbox, .MuiDataGrid-cellCheckbox': {
        display: 'none',
      },
      '& .MuiDataGrid-colCellTitle': {
        fontWeight: 'bold',
        fontSize: '1.3em',
      },
      '& .MuiDataGrid-cellCenter': {
        justifyContent: 'center',
      },
      '& .MuiFormControl-root': {
        boxSizing: 'initial',
      },
      '& .MuiDataGrid-columnsContainer': {
        backgroundColor: grey['300'],
      },
      '& .Mui-odd': {
        backgroundColor: grey['50'],
      },
      '& .MuiDataGrid-colCell, .MuiDataGrid-cell': {
        borderRight: `1px solid ${blueGrey['100']}`,
      },
      '& .MuiDataGrid-row:hover, .MuiDataGrid-row.Mui-selected:hover': {
        backgroundColor: blue['100'],
      },
      '& .MuiDataGrid-row.Mui-selected': {
        backgroundColor: green['50'],
      },
    },
  }))();

  return (
    <div
      ref={divRef}
      {...rootProps}
      style={{ display: 'flex', height: '100%', ...rootProps.style }}
    >
      <div style={{ flexGrow: 1 }}>
        <DataGrid
          className={classes.root}
          /** 若 autoHeight 會因 cell 多了 vertical scrollbar，而未與 header 對齊 */
          // localeText={ZH_TW}
          // autoHeight
          checkboxSelection
          disableSelectionOnClick
          density={density}
          disableColumnMenu={disableColumnMenu}
          /** 設定page */
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={[...rowsPerPageOptions, pageSize]
            .filter((v, i, array) => array.indexOf(v) === i)
            .sort((a, b) => a - b)}
          {...other}
          /** data 經處理後 */
          rows={rows}
          columns={columns.map((obj) => ({ headerAlign, align, ...obj }))}
          /** 設定選取的 RowId，需開啟 checkboxSelection，才有整列選取 style */
          selectionModel={selectionModel}
          /** checkboxSelection change 時觸發，有不同時，會更新 rows 及 selected */
          onSelectionModelChange={handleSelectionModelChange}
          /** 要更新 local state，也要支援 props.onPageChange */
          onPageChange={(param) => {
            const { page: pageTemp2 } = param;
            setPage(pageTemp2);

            if (onPageChange) {
              onPageChange(param);
            }
          }}
          onPageSizeChange={(param) => {
            const {
              page: pageTemp2,
              pageSize: pageSizeTemp2,
              rowCount,
            } = param;
            setPageSize(pageSizeTemp2);

            const maxPage = Math.floor(rowCount / pageSizeTemp2);
            const isOver = pageTemp2 > maxPage;
            if (isOver) {
              setPage(maxPage);
            }

            if (onPageSizeChange) {
              onPageSizeChange({
                ...param,
                page: isOver ? maxPage : pageTemp2,
              });
            }
          }}
        />
      </div>
    </div>
  );
};

export default Table;
